import {
  Users, BookOpen, Clock, Radio, FileEdit, Eye,
  Calendar, Globe2, Layers, Zap, FlaskConical, GraduationCap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { currencySymbol } from "@/lib/currency";
import type { PricingPlan, OfferingType } from "@/types/offering";
import { useTeachingModes, useGradeBands, useCurricula } from "@/hooks/useValueCatalog";
import { getOfferingTypeTheme } from "@/lib/offeringTypeTheme";

export type OfferingStatus = "DRAFT" | "PENDING_REVIEW" | "PUBLISHED" | "UNPUBLISHED" | "DELETED";

export interface Offering {
  id: string;
  title: string;
  type: "1-on-1 Coach" | "Group Coaching" | "Crash Course" | "Specialized Program";
  subject: string;
  topics?: string[];
  pricingPlans: PricingPlan[];
  duration: string;
  description: string;
  status: OfferingStatus;
  active: boolean;
  studentsEnrolled?: number;
  maxStudents?: number;
  sessionsCount?: number;
  teachingMode?: string;
  gradeBands?: string[];
  curriculumSystems?: string[];
  trialSessionAvailable?: boolean;
  learningOutcomes?: string;
  prerequisites?: string;
  targetScorePromise?: string;
  earlyBirdDeadline?: string | null;
  sessionsPerWeek?: number;
  totalSessionsPlanned?: number;
  nextBatchStartDate?: string;
  batchSizeMin?: number;
  batchSizeMax?: number;
  seatsRemaining?: number;
  currentEnrollment?: number;
  courseStartDate?: string;
  courseEndDate?: string;
  totalSessions?: number;
  seatsTotal?: number;
  studyMaterialIncluded?: boolean;
  mockTestsIncluded?: number;
}

/** This card's `type` uses human-readable labels rather than the canonical `OfferingType` codes. */
const LABEL_TO_OFFERING_TYPE: Record<string, OfferingType> = {
  "1-on-1 Coach": "ONE_TO_ONE",
  "Group Coaching": "GROUP",
  "Crash Course": "CRASH_COURSE",
};

const TYPE_LABELS: Record<string, string> = {
  "1-on-1 Coach": "1-on-1 Session",
  "Group Coaching": "Group Class",
  "Crash Course": "Crash Course",
  "Specialized Program": "Program",
};

/** Per-type drop shadow tint, matched to the shared theme's hue family. */
const GLOW: Record<OfferingType, string> = {
  ONE_TO_ONE: "hover:shadow-[0_8px_30px_rgba(4,120,87,0.15)]",
  GROUP: "hover:shadow-[0_8px_30px_rgba(109,40,217,0.15)]",
  CRASH_COURSE: "hover:shadow-[0_8px_30px_rgba(180,83,9,0.15)]",
};

/**
 * "Specialized Program" isn't one of the three canonical offering types, so it
 * keeps its own neutral identity rather than borrowing (and clashing with) one
 * of the three colours reserved for 1-on-1 / Group / Crash course.
 */
const SPECIALIZED_PROGRAM_THEME = {
  icon: BookOpen,
  gradient: "from-slate-700 to-slate-600",
  cardBg: "bg-slate-50/40",
  accentText: "text-slate-700",
  accentLight: "bg-slate-100 border-slate-200",
  topicBg: "bg-slate-100 text-slate-700 border-slate-200",
  glow: "hover:shadow-[0_8px_30px_rgba(51,65,85,0.15)]",
};

function offeringTypeVisual(label: string) {
  const canonicalType = LABEL_TO_OFFERING_TYPE[label];
  const displayLabel = TYPE_LABELS[label] ?? label;
  if (!canonicalType) return { ...SPECIALIZED_PROGRAM_THEME, label: displayLabel };

  const theme = getOfferingTypeTheme(canonicalType);
  return {
    icon: theme.icon,
    label: displayLabel,
    gradient: theme.gradient,
    cardBg: theme.soft,
    accentText: theme.accent,
    accentLight: cn(theme.soft, "border", theme.border),
    topicBg: cn(theme.pill, "border", theme.border),
    glow: GLOW[canonicalType],
  };
}

const typeConfig: Record<string, ReturnType<typeof offeringTypeVisual>> = {
  "1-on-1 Coach": offeringTypeVisual("1-on-1 Coach"),
  "Group Coaching": offeringTypeVisual("Group Coaching"),
  "Crash Course": offeringTypeVisual("Crash Course"),
  "Specialized Program": offeringTypeVisual("Specialized Program"),
};

const statusConfig: Record<OfferingStatus, {
  label: string; dot: string; text: string; bg: string; border: string;
  icon: typeof Radio; pulse?: boolean;
}> = {
  PUBLISHED:      { label: "Live",         dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-100", icon: Radio,    pulse: true },
  DRAFT:          { label: "Draft",        dot: "bg-amber-400",   text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-100",   icon: FileEdit },
  UNPUBLISHED:    { label: "Unpublished",  dot: "bg-slate-400",   text: "text-slate-500",   bg: "bg-slate-100",  border: "border-slate-200",   icon: Eye },
  PENDING_REVIEW: { label: "Under Review", dot: "bg-sky-400",     text: "text-sky-700",     bg: "bg-sky-50",     border: "border-sky-100",     icon: Eye },
  DELETED:        { label: "Deleted",      dot: "bg-red-400",     text: "text-red-600",     bg: "bg-red-50",     border: "border-red-100",     icon: Eye },
};

const statusSubtext: Partial<Record<OfferingStatus, string>> = {
  PUBLISHED:      "Visible to students",
  DRAFT:          "Not visible to students",
  PENDING_REVIEW: "Awaiting approval",
  UNPUBLISHED:    "Hidden from students",
};

function formatDate(s?: string | null): string | null {
  if (!s) return null;
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return s; }
}

interface TeacherOfferingCardProps {
  offering: Offering;
  onEdit: (id: string) => void;
}

export function TeacherOfferingCard({ offering, onEdit }: TeacherOfferingCardProps) {
  const config = typeConfig[offering.type] ?? typeConfig["1-on-1 Coach"];
  const Icon = config.icon;
  const status = statusConfig[offering.status] ?? statusConfig.DRAFT;
  const StatusIcon = status.icon;
  const isDraft = offering.status !== "PUBLISHED";

  const teachingModes = useTeachingModes();
  const gradeBands = useGradeBands();
  const curricula = useCurricula();

  // Meta items
  type MetaItem = { icon: typeof Clock; label: string; value: string; highlight?: boolean };
  const metaItems: MetaItem[] = [];
  if (offering.duration)           metaItems.push({ icon: Clock,        label: "Duration",    value: offering.duration });
  if (offering.teachingMode)       metaItems.push({ icon: Globe2,       label: "Mode",        value: teachingModes.labelOf(offering.teachingMode) });
  if (offering.sessionsPerWeek)    metaItems.push({ icon: Layers,       label: "Frequency",   value: `${offering.sessionsPerWeek}× / week` });
  if (offering.totalSessionsPlanned) metaItems.push({ icon: Layers,     label: "Sessions",    value: `${offering.totalSessionsPlanned}` });
  if (offering.totalSessions)      metaItems.push({ icon: Layers,       label: "Sessions",    value: `${offering.totalSessions}` });
  if (offering.nextBatchStartDate) metaItems.push({ icon: Calendar,     label: "Next batch",  value: formatDate(offering.nextBatchStartDate) ?? "" });
  if (offering.courseStartDate)    metaItems.push({ icon: Calendar,     label: "Starts",      value: formatDate(offering.courseStartDate) ?? "" });
  if (offering.courseEndDate)      metaItems.push({ icon: Calendar,     label: "Ends",        value: formatDate(offering.courseEndDate) ?? "" });
  if (offering.batchSizeMin != null && offering.batchSizeMax != null)
    metaItems.push({ icon: Users, label: "Batch size", value: `${offering.batchSizeMin}–${offering.batchSizeMax}` });
  if (offering.seatsTotal != null)        metaItems.push({ icon: Users,       label: "Seats",       value: `${offering.seatsTotal}` });
  if (offering.currentEnrollment != null) metaItems.push({ icon: Users,       label: "Enrolled",    value: `${offering.currentEnrollment}` });
  if (offering.seatsRemaining != null)    metaItems.push({ icon: Users,       label: "Seats left",  value: `${offering.seatsRemaining}`, highlight: offering.seatsRemaining <= 5 });
  if (offering.mockTestsIncluded)         metaItems.push({ icon: FlaskConical, label: "Mock tests", value: `${offering.mockTestsIncluded}` });
  if (offering.earlyBirdDeadline)         metaItems.push({ icon: Calendar,    label: "Early bird",  value: formatDate(offering.earlyBirdDeadline) ?? "", highlight: true });

  const tags = [
    ...(offering.topics?.slice(0, 3).map(t => ({ label: t, kind: "topic" })) ?? []),
    ...(offering.subject ? [{ label: offering.subject, kind: "subject" }] : []),
    ...(offering.gradeBands?.map(g => ({ label: gradeBands.labelOf(g), kind: "grade" })) ?? []),
    ...(offering.curriculumSystems?.map(c => ({ label: curricula.labelOf(c), kind: "curriculum" })) ?? []),
    ...(offering.studyMaterialIncluded ? [{ label: "Study material", kind: "material" }] : []),
    ...(offering.trialSessionAvailable ? [{ label: "Free trial", kind: "trial" }] : []),
  ];

  return (
    <div
      onClick={() => onEdit(offering.id)}
      className={cn(
        "group cursor-pointer rounded-3xl border border-slate-200 shadow-sm overflow-hidden",
        "transition-all duration-300 hover:-translate-y-1 h-full flex flex-col",
        isDraft ? "bg-slate-50 hover:shadow-md" : cn(config.cardBg, config.glow),
      )}
    >
      {/* ── Gradient header band ── */}
      <div className={cn(
        "bg-gradient-to-r px-5 py-4",
        isDraft ? "from-slate-500 to-slate-600" : config.gradient,
      )}>
        <div className="flex items-start justify-between gap-3">
          {/* Icon + title */}
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-bold uppercase tracking-widest text-white/60">
                  {config.label}
                </span>
                {offering.trialSessionAvailable && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-amber-400/25 text-amber-200 border border-amber-300/30">
                    <Zap className="w-2.5 h-2.5" /> Trial
                  </span>
                )}
              </div>
              <h3 className="text-base font-bold text-white leading-snug line-clamp-2">
                {offering.title}
              </h3>
              {offering.subject && (
                <p className="text-xs text-white/60 mt-0.5 font-medium">{offering.subject}</p>
              )}
            </div>
          </div>

          {/* Status badge + subtext */}
          <div className="flex flex-col items-end gap-1 shrink-0">
            <div className={cn(
              "inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border",
              status.bg, status.border, status.text,
            )}>
              <span className={cn("w-1.5 h-1.5 rounded-full", status.dot, status.pulse && "animate-pulse")} />
              <StatusIcon className={cn("w-3 h-3", status.pulse && "animate-pulse")} />
              {status.label}
            </div>
            {statusSubtext[offering.status] && (
              <span className="text-xs font-semibold text-white/60">
                {statusSubtext[offering.status]}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="px-5 py-3 flex flex-col gap-3 flex-1">

        {/* Description */}
        {offering.description && (
          <p
            className="text-sm text-slate-700 leading-relaxed line-clamp-2"
            dangerouslySetInnerHTML={{ __html: offering.description }}
          />
        )}

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag, i) => (
              <span key={i} className={cn(
                "inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border",
                tag.kind === "trial"    ? "bg-amber-50 text-amber-700 border-amber-200" :
                tag.kind === "material" ? "bg-sky-50 text-sky-700 border-sky-100" :
                tag.kind === "subject"  ? "bg-indigo-50 text-indigo-700 border-indigo-200" :
                tag.kind === "grade" || tag.kind === "curriculum" ? "bg-slate-100 text-slate-700 border-slate-200" :
                isDraft ? "bg-slate-100 text-slate-700 border-slate-200" : config.topicBg,
              )}>
                {tag.kind === "subject"  && <BookOpen className="w-3 h-3" />}
                {tag.kind === "grade"    && <GraduationCap className="w-3 h-3" />}
                {tag.kind === "trial"    && <Zap className="w-3 h-3" />}
                {tag.label}
              </span>
            ))}
            {(offering.topics?.length ?? 0) > 3 && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full border bg-slate-100 text-slate-700 border-slate-200">
                +{offering.topics!.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Meta items — 2-column grid */}
        {metaItems.length > 0 && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-2 border-t border-slate-100">
            {metaItems.map((item, i) => {
              const MetaIcon = item.icon;
              return (
                <div key={i} className="flex items-center gap-2 min-w-0">
                  <div className={cn(
                    "w-7 h-7 rounded-lg flex items-center justify-center border shrink-0",
                    item.highlight
                      ? "bg-amber-50 border-amber-100"
                      : isDraft ? "bg-slate-100 border-slate-200" : config.accentLight,
                  )}>
                    <MetaIcon className={cn(
                      "w-3.5 h-3.5",
                      item.highlight ? "text-amber-600" : isDraft ? "text-slate-400" : config.accentText,
                    )} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold leading-none">{item.label}</p>
                    <p className={cn("text-sm font-semibold truncate", item.highlight ? "text-amber-700" : "text-slate-900")}>
                      {item.value}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {/* ── Footer — multi-currency pricing ── */}
      <div className="px-5 py-3 border-t border-slate-200/60 bg-white/40">
        {offering.pricingPlans.length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Per session</p>
            {offering.pricingPlans.map((plan) => {
              const sym = currencySymbol(plan.currency);
              const perSession = plan.perSessionEquivalent ?? plan.regularPrice;
              return (
                <div key={plan.currency} className="flex items-baseline gap-1.5 flex-wrap">
                  <span className="text-lg font-extrabold text-slate-900 leading-none">
                    {sym}{perSession.toLocaleString()}
                  </span>
                  {plan.earlyBirdPrice != null && plan.earlyBirdPrice > 0 && (
                    <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full -rotate-2 inline-block animate-bounce hover:animate-none transition-transform hover:scale-105">
                      Early bird {sym}{(plan.earlyBirdPerSessionEquivalent ?? plan.earlyBirdPrice).toLocaleString()}
                    </span>
                  )}
                  {plan.trialSessionPrice != null && plan.trialSessionPrice > 0 && (
                    <span className="text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full">
                      Trial {sym}{plan.trialSessionPrice.toLocaleString()}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No pricing set</p>
        )}
      </div>
    </div>
  );
}
